import { CertificationStatus, CertificationStatusSummary } from './certification.models';

export type CertificationState = 'in-progress' | 'course-complete' | 'requirements-pending' | 'ready-to-certify' | 'certified-current' | 'certified-lapsed';

/**
 * KAN-560: the minimum a surface must know to name a certification state.
 *
 * Both shapes satisfy it structurally — the full `CertificationStatus` from the
 * single-learner endpoint and the compact `CertificationStatusSummary` from the
 * batch endpoint — so the matrix cell, the member drawer, the progress panel and
 * the learner modal all derive their state from ONE function. `requirements` is
 * optional precisely because the compact summary has none; nothing below may
 * fabricate a count when it is absent.
 */
export interface CertificationFacts {
  courseCompleted: boolean;
  certificationEligible: boolean;
  certified: boolean;
  currentlyCompliant: boolean;
  requirements?: Array<{ satisfied?: boolean }>;
}

export function certificationState(facts: CertificationFacts): CertificationState {
  if (facts.certified) return facts.currentlyCompliant ? 'certified-current' : 'certified-lapsed';
  if (!facts.courseCompleted) return 'in-progress';
  if (facts.certificationEligible) return 'ready-to-certify';
  // Absent requirements (the compact summary) still means "complete but not
  // eligible", which can only be an unmet requirement.
  return !facts.requirements || facts.requirements.length ? 'requirements-pending' : 'course-complete';
}

export function certificationLabel(facts: CertificationFacts): string {
  const state = certificationState(facts);
  switch (state) {
    case 'certified-current': return 'Certified';
    case 'certified-lapsed': return 'Certified · evidence lapsed';
    case 'ready-to-certify': return 'Ready to certify';
    case 'requirements-pending': {
      // Only the full status can say HOW MANY are met. The compact summary
      // deliberately renders a countless "Pending" rather than inventing one.
      if (!facts.requirements) return 'Pending';
      const met = facts.requirements.filter(requirement => requirement.satisfied).length;
      return `Pending · ${met} of ${facts.requirements.length} met`;
    }
    case 'course-complete': return 'Course complete';
    default: return 'Not yet eligible';
  }
}

export function certificationBadgeClass(facts: CertificationFacts): string {
  switch (certificationState(facts)) {
    case 'certified-current': return 'badge-success';
    case 'certified-lapsed':
    case 'requirements-pending': return 'badge-warning badge-outline';
    case 'ready-to-certify': return 'badge-info';
    case 'course-complete': return 'badge-neutral';
    default: return 'badge-ghost';
  }
}

export function requirementNeedsAttention(requirement: CertificationStatus['requirements'][number]): boolean {
  if (requirement.satisfied) return false;
  const detail = (requirement.detail || '').toLowerCase();
  return !requirement.name?.trim() || /deleted|missing target|no longer exists|unavailable/.test(detail);
}

/**
 * KAN-560: the batch summary collapses the whole requirement list to one
 * `needsAttention` boolean, computed server-side with the same rule as
 * `requirementNeedsAttention` above. This reads it back without needing the
 * list, so a matrix cell can flag a broken configuration.
 */
export function summaryNeedsAttention(summary: Pick<CertificationStatusSummary, 'needsAttention'>): boolean {
  return summary.needsAttention === true;
}

/** KAN-625: shown whenever an override expiry is refused, by this rule or by the API. */
export const OVERRIDE_EXPIRY_MESSAGE = 'Choose an expiry date after today, or leave it blank.';

const pad2 = (value: number) => String(value).padStart(2, '0');

/**
 * KAN-625: an override expiry (the `YYYY-MM-DD` a date input yields) must be a
 * calendar day strictly after the instructor's LOCAL today. Blank means "never
 * expires" and is valid.
 *
 * Compared as calendar strings on purpose: `new Date('YYYY-MM-DD')` parses as
 * UTC midnight, which in US zones is the previous local evening, so the old
 * Date-based check refused "tomorrow" late in the day.
 */
export function overrideExpiryIsValid(expiresAt: string, today: Date = new Date()): boolean {
  if (!expiresAt) return true;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(expiresAt);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const parsed = new Date(year, month - 1, day);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) return false;
  const todayKey = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
  return expiresAt > todayKey;
}

/**
 * KAN-625: the API refuses an expiry at or before ITS now (400, "Override
 * expiration must be a valid future date"). That can still happen after the
 * local rule passes, so the dialog must recognise it rather than show a
 * generic failure.
 */
export function isOverrideExpiryRejection(error: unknown): boolean {
  const message = (error as { error?: { error?: { message?: unknown } } } | null)?.error?.error?.message;
  return typeof message === 'string' && /override expiration/i.test(message);
}
